// SPDX-License-Identifier: BUSL-1.1

/**
 * Dashboard home page with summary cards and recent activity.
 *
 */

import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { apiFetch, API_BASE, shouldRetryApiQuery } from '@/lib/api-client';
import {
  AUDIT_RECENT_5_QUERY_KEY,
  CONFIGS_QUERY_KEY,
  FLAGS_QUERY_KEY,
  HEALTH_QUERY_KEY,
  queryKeyLabel,
} from '@/lib/bootstrap-query-keys';
import { useOrg } from '@/contexts/org-context';
import {
  type HealthResponse,
  healthToCardProps,
} from '@/lib/health';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table';
import { DashboardSkeleton } from '@/components/skeletons';
import {
  type AuditEntry,
  type AuditResponse,
  ACTOR_BADGE,
  ACTION_COLORS,
  actionSuffix,
  type Flag,
} from '@/types/api';
import { relativeTime } from '@/lib/format';

/** Value-text color per card tone. Card chrome stays neutral. */
const TONE_COLORS: Record<'neutral' | 'ok' | 'warning' | 'error', string> = {
  neutral: 'text-neutral-100',
  ok: 'text-green-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
};

/**
 * Dashboard summary card. `href` that starts with `http(s)://` is rendered
 * as a raw `<a>` pointing at the absolute URL (used for the System Health
 * card, which has to hit the Worker origin — NOT the Pages origin — or it
 * falls through to the SPA catchall). Relative hrefs use React Router.
 *
 * `tone` controls the value-text color. Used by System Health to
 * visually distinguish ok / degraded / error from the baseline neutral.
 */
function Card({
  title,
  value,
  sub,
  href,
  tone = 'neutral',
}: {
  title: string;
  value: string;
  sub: string;
  href: string;
  tone?: 'neutral' | 'ok' | 'warning' | 'error';
}) {
  const inner = (
    <>
      <p className="text-sm text-neutral-400">{title}</p>
      <p className={`mt-1 text-2xl font-bold ${TONE_COLORS[tone]}`}>{value}</p>
      <p className="mt-1 text-xs text-neutral-500">{sub}</p>
    </>
  );
  const className =
    'block rounded-lg border border-neutral-800 bg-neutral-900 p-5 hover:border-neutral-700';

  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {inner}
    </Link>
  );
}

const activityColumns: ColumnDef<AuditEntry>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Timestamp',
    cell: ({ row }) => (
      <span className="text-neutral-400" title={row.original.createdAt}>
        {relativeTime(row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: 'actorId',
    header: 'Actor',
    cell: ({ row }) => (
      <span>
        <span className="text-neutral-200">{row.original.actorId}</span>
        <span className={`ml-2 rounded px-2 py-0.5 text-xs ${ACTOR_BADGE[row.original.actorType] ?? ACTOR_BADGE.system}`}>
          {row.original.actorType}
        </span>
      </span>
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => (
      <span className={`font-mono ${ACTION_COLORS[actionSuffix(row.original.action)] ?? 'text-neutral-300'}`}>
        {row.original.action}
      </span>
    ),
  },
  {
    id: 'resource',
    header: 'Resource',
    cell: ({ row }) => (
      <span className="text-neutral-300">
        {row.original.resourceType}/{row.original.resourceId}
      </span>
    ),
  },
];

export function DashboardV1() {
  const { bootstrapReady } = useOrg();
  const configs = useQuery({
    queryKey: CONFIGS_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () =>
      apiFetch<{ key: string }[]>('/configs', { _queryKey: queryKeyLabel(CONFIGS_QUERY_KEY) }),
    retry: shouldRetryApiQuery,
  });
  const flags = useQuery({
    queryKey: FLAGS_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () => apiFetch<Flag[]>('/flags', { _queryKey: queryKeyLabel(FLAGS_QUERY_KEY) }),
    retry: shouldRetryApiQuery,
  });
  const audit = useQuery({
    queryKey: AUDIT_RECENT_5_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () =>
      apiFetch<AuditResponse>('/audit?limit=5', {
        _queryKey: queryKeyLabel(AUDIT_RECENT_5_QUERY_KEY),
      }),
    retry: shouldRetryApiQuery,
  });
  // Poll health every 30s so operators see degraded / error states within
  // one refresh cycle without having to reload the page.
  const health = useQuery({
    queryKey: HEALTH_QUERY_KEY,
    enabled: bootstrapReady,
    queryFn: () =>
      apiFetch<HealthResponse>('/health', { _queryKey: queryKeyLabel(HEALTH_QUERY_KEY) }),
    refetchInterval: 30_000,
    retry: shouldRetryApiQuery,
  });

  const enabledCount = flags.data?.filter((f) => f.enabled).length ?? 0;
  const totalFlags = flags.data?.length ?? 0;
  const recentCount = audit.data?.entries.filter(
    (e) => Date.now() - new Date(e.createdAt).getTime() < 86_400_000,
  ).length ?? 0;

  const isLoading = configs.isLoading || flags.isLoading || audit.isLoading;
  if (isLoading) return <DashboardSkeleton />;

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-neutral-100">Dashboard</h1>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Config Surfaces" value={String(configs.data?.length ?? '-')} sub="Registered surfaces" href="/configs" />
        <Card title="Feature Flags" value={`${enabledCount}/${totalFlags}`} sub="Enabled / total" href="/flags" />
        <Card title="Recent Changes" value={String(recentCount)} sub="Last 24 hours" href="/audit" />
        {/*
          NOTE: In local dev API_BASE is '/api' (relative), so clicking the
          card still hits React Router's catchall. Open the URL directly or
          set VITE_API_URL=http://localhost:8787/api in .env.local to wire
          the card to the local Worker.
        */}
        <Card
          title="System Health"
          href={`${API_BASE}/health`}
          {...healthToCardProps(health.data, health.error)}
        />
      </div>

      <div className="mb-6 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => { configs.refetch(); flags.refetch(); audit.refetch(); }}>
          Refresh
        </Button>
      </div>

      <h2 className="mb-4 text-lg font-semibold text-neutral-200">Recent Activity</h2>

      <DataTable columns={activityColumns} data={audit.data?.entries ?? []} pageSize={5} emptyMessage="No recent activity." />
    </div>
  );
}
